import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Button
} from "@mui/material";
import { ArrowBack, Close } from "@mui/icons-material";
import BotUserProfile from "../ChatMessenger/BotUserProfile";
import RealUserProfile from "../ChatMessenger/RealUserProfile";
import BotOnlyModal from "./BotOnlyModal";
import { fetchUserData } from "../../api/user";

const UserInitiateModal = ({ open, onClose, userId }) => {

  const [step, setStep] = useState(2);
  const steps = ['Select Bot', 'Initiate Chat'];
  const [openProfile, setOpenProfile] = useState(false);
  const [openRealProfile, setOpenRealProfile] = useState(false);

  const toggleDrawer = (open) => () => {
    setOpenProfile(open);
  };
  const toggleRealUserDrawer = (open) => () => {
    setOpenRealProfile(open);
  };
  const [userData, setUserData] = useState(false);
  const [selectedBot, setSelectedBot] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const fetchData = async () => {
    try {
      setLoading(true)
      setLoadError(false)
      // isAbort:false — the profile modal reads the same /admin/user/:id and
      // would otherwise cancel this request, leaving the dialog silently blank.
      const user = await fetchUserData(userId, setUserData, false, undefined, { isAbort: false });
      // `/admin/user/:id` now falls back to the account-delete log rather than
      // 404ing, so a purged member resolves here instead of failing. Viewing
      // their archived profile is fine; starting a bot conversation with an
      // account that no longer exists is not, so this dialog still refuses —
      // `loadError` carries the reason so the copy can say which it is.
      if (!user) setLoadError(true);
      else if (user.isDeletedAccount) setLoadError('deleted');
    } catch (error) {
      console.error('Error while loading real-time data', error);
      setLoadError(true);
    }
    finally {
      setLoading(false);
    }
  };

  // Keyed on `open` as well as `userId`: fetching while closed wasted a request
  // on every row-menu click, and re-opening the SAME user never refetched — so a
  // single failed load stayed blank until a different user was picked.
  useEffect(() => {
    if (!open) return;
    if (userId) {
      fetchData();
    } else {
      setLoading(false);
      setLoadError(true);
    }
  }, [open, userId]);
  useEffect(()=>{
    setStep(2)
  },[open])
  const handleBotSelect = (bot) => {
    setSelectedBot(bot);
    setOpenProfile(true);
    // setStep(3);
  };
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {/* --- MOVED HEADER HERE --- */}
        <Box
          sx={{
            p: 3,
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "#fff",
            borderBottom: "1px solid #ddd",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => {
                if (step === 1) onClose();
                else if (step === 2) onClose(); // optional: close modal
                else setStep(2); // step 3 → step 2
              }}
            >
              <ArrowBack />
            </IconButton>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Initiate New Chat
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Search for a user to start a conversation
              </Typography>
            </Box>

            <IconButton onClick={onClose} sx={{ bgcolor: "grey.300" }}>
              <Close />
            </IconButton>
          </Box>
          <Stepper
            // This modal's flow starts at step 2 (bot selection) and runs to 3,
            // against two labels — so the offset is 2, not 1. With `step - 1`,
            // opening the dialog rendered "Select Bot" as already completed
            // while it was the step you were actually on.
            activeStep={step - 2}
            sx={{
              mt: 2,

              // COMPLETED step icon color
              '& .MuiStepIcon-root.Mui-completed': {
                color: '#BF8500', // green
              },

              // ACTIVE step icon color
              '& .MuiStepIcon-root.Mui-active': {
                color: '#ff9800', // orange
              },

              // INACTIVE (default) step icon color
              '& .MuiStepIcon-root': {
                color: '#4A5565', // grey 
              },

              // ACTIVE label color
              '& .MuiStepLabel-label.Mui-active': {
                color: '#ff9800 !important',
                fontWeight: 600,
              },
              '& .Mui-active .MuiStepConnector-line': { borderColor: '#BF8500 !important' },

              // COMPLETED label color
              '& .MuiStepLabel-label.Mui-completed': {
                color: '#BF8500 !important',
                fontWeight: 600,
              },

              // INACTIVE label color
              '& .MuiStepLabel-label': {
                color: '#9e9e9e',
                fontSize: '12px',
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>
                  <Typography fontSize="12px">{label}</Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

        </Box>

        {/* BODY */}
        <DialogContent
          dividers
          sx={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          {loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Loading...
              </Typography>
            </Box>
          )}

          {/* Without this branch a failed load rendered an empty white dialog —
              BotOnlyModal has no output when selectedUser is falsy. */}
          {!loading && (loadError || !userData) && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                {loadError === 'deleted' ? 'This member has deleted their account.' : 'Couldn’t load this member.'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {loadError === 'deleted'
                  ? 'A conversation can’t be started with a deleted account. Their archived profile is still viewable from Deleted Accounts.'
                  : 'The request failed or returned no profile. Please try again.'}
              </Typography>
              {loadError !== 'deleted' && (
                <Button variant="outlined" onClick={fetchData} disabled={!userId}>
                  Retry
                </Button>
              )}
            </Box>
          )}

          {!loading && !loadError && userData && (
            <BotOnlyModal
              selectedUser={userData}
              step={step}
              setStep={setStep}
              onClose={onClose}
              onBotViewButtonClick={handleBotSelect}
              onRealUserClick={toggleRealUserDrawer(true)}
            />
          )}
        </DialogContent>
      </Dialog>
      <BotUserProfile
        open={openProfile}
        toggleDrawer={toggleDrawer}
        selectedBot={selectedBot}
      />
      <RealUserProfile
        open={openRealProfile}
        toggleDrawer={toggleRealUserDrawer}
        userData={userData}
      />
    </>
  );
};

export default UserInitiateModal;
